// Retail Overview: per-game numbers and time series, built from the same API responses
// the Overview already fetches (occupancy, door counts, sales counters, queue times).
import moment from 'moment';
import { generateDay, formatTimestamp } from '../../../util.js';

// "HH:mm" -> minute of the store day (times before 3 AM belong to the previous day)
export const toDayMinute = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h < 3 ? h + 24 : h) * 60 + m;
};

// Timestamp "YYYY-MM-DDTHH:mm..." -> minute of the store day for `date`
const timestampMinute = (timestamp, date) => {
  if (!timestamp || !timestamp.includes('T')) return null;
  const [datePart, timePart] = timestamp.split('T');
  const [h, m] = timePart.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const nextDay = datePart > moment(date).format('YYYY-MM-DD');
  return (nextDay ? 1440 : 0) + h * 60 + m;
};

export const minuteToClock = (minute) =>
  moment().startOf('day').add(minute, 'minutes').format('h:mm A');

// Occupancy response -> one row per minute of the day (same shape the game-day views use).
// Uses map lookups instead of scanning every reading for every zone at every minute.
export const processOccupancy = (data_, currentLocation, dateToFetch) => {
  const zones = currentLocation.zones.map((zone) => {
    const entry = (data_ || []).find((e) => e[zone.id] !== undefined);
    const readings = entry ? entry[zone.id] : [];
    return {
      zoneId: zone.id,
      byTime: new Map(readings.map((item) => [item?.time || '', item?.occupancy || 0])),
    };
  });

  return generateDay(dateToFetch).map((currentMinute, x) => {
    const formattedTime = formatTimestamp(new Date(currentMinute));
    const traffic = zones
      .filter((z) => z.byTime.has(formattedTime))
      .map((z) => ({ traffic: z.byTime.get(formattedTime), id: z.zoneId }));
    const total = traffic.reduce((sum, item) => sum + item.traffic, 0);
    return {
      time: x,
      time2: currentMinute,
      total,
      perc: Math.round((total / currentLocation.maxOccupancy) * 100),
      traffic,
    };
  });
};

// Door counts by phase: same rules as the game-day Counter (counts are cumulative)
const phaseCounts = (trafficResult, date, gatesOpen, gameStartTime) => {
  let pregame = 0;
  let gates = 0;
  let game = 0;
  const day = moment(date).format('YYYY-MM-DD');
  (trafficResult || []).slice(1).forEach((entry) => {
    if (!entry?.timestamp) return;
    const [datePart, timePart] = entry.timestamp.split('T');
    const entryTime = timePart.slice(0, 5);
    const entryHour = parseInt(timePart.split(':')[0], 10);
    const isGameDay = datePart === day;
    const isEarlyNextDay = datePart > day && entryHour < 3;
    if (!isGameDay && !isEarlyNextDay) return;
    if (isEarlyNextDay) {
      game = Math.abs(pregame + gates - entry.count);
      return;
    }
    if (entryTime < gatesOpen) pregame = entry.count;
    else if (entryTime >= gatesOpen && entryTime < gameStartTime) gates = Math.abs(pregame - entry.count);
    else if (entryTime >= gameStartTime) game = Math.abs(pregame + gates - entry.count);
  });
  return { pregame, gates, game };
};

// Estimated dwell per zone: the same adjacency-enhanced Little's Law the Charts tab map uses
// (dwell = average occupancy / estimated flow), over minutes [startMinute, endMinute].
// Returns { [zoneId]: { avg, peak, peakMinute } }.
export const estimateZoneDwell = (rows, zoneIds, adjacencyMap, startMinute, endMinute) => {
  const estimates = {};
  if (!rows || endMinute - startMinute < 15) return estimates;

  const allSeries = {};
  zoneIds.forEach((zoneId) => {
    const series = [];
    for (let t = startMinute; t <= endMinute; t += 1) {
      const entry = rows[t]?.traffic?.find((z) => z.id === zoneId);
      series.push(entry?.traffic || 0);
    }
    allSeries[zoneId] = series;
  });

  const allDeltas = {};
  zoneIds.forEach((zoneId) => {
    const series = allSeries[zoneId];
    const deltas = [0];
    for (let i = 1; i < series.length; i += 1) deltas.push(series[i] - series[i - 1]);
    allDeltas[zoneId] = deltas;
  });

  const WINDOW_SIZE = 15;
  const MIN_FLOW_THRESHOLD = 0.5;

  zoneIds.forEach((zoneId) => {
    const series = allSeries[zoneId];
    const deltas = allDeltas[zoneId];
    if (series.length < 2) {
      estimates[zoneId] = { avg: null, peak: null, peakMinute: null };
      return;
    }

    const stepFlow = [0];
    if (adjacencyMap && adjacencyMap[zoneId]) {
      const neighbors = adjacencyMap[zoneId];
      for (let i = 1; i < deltas.length; i += 1) {
        const ownDelta = deltas[i];
        const ownFlow = Math.abs(ownDelta);
        let adjacencyFlow = 0;
        neighbors.forEach((neighborId) => {
          if (!allDeltas[neighborId]) return;
          const neighborDelta = allDeltas[neighborId][i];
          if (ownDelta < 0 && neighborDelta > 0) {
            adjacencyFlow += Math.min(Math.abs(ownDelta), neighborDelta);
          } else if (ownDelta > 0 && neighborDelta < 0) {
            adjacencyFlow += Math.min(ownDelta, Math.abs(neighborDelta));
          } else if (ownDelta === 0 && neighborDelta !== 0) {
            const neighborConnections = adjacencyMap[neighborId]?.length || 1;
            adjacencyFlow += Math.abs(neighborDelta) / neighborConnections;
          }
        });
        stepFlow.push(Math.max(ownFlow, adjacencyFlow));
      }
    } else {
      for (let i = 1; i < deltas.length; i += 1) stepFlow.push(Math.abs(deltas[i]));
    }

    let totalOccupancy = 0;
    let totalFlow = 0;
    let activeSteps = 0;
    for (let i = 0; i < series.length; i += 1) {
      if (stepFlow[i] >= MIN_FLOW_THRESHOLD) {
        totalOccupancy += series[i];
        totalFlow += stepFlow[i];
        activeSteps += 1;
      }
    }
    const avgOccupancy = activeSteps > 0 ? totalOccupancy / activeSteps : 0;
    const flowRate = activeSteps > 0 ? totalFlow / activeSteps : 0;
    const avgDwell = flowRate > 0 ? Math.round(avgOccupancy / flowRate) : null;

    let peakDwell = null;
    let peakMinuteIndex = null;
    if (series.length >= WINDOW_SIZE) {
      for (let start = 0; start <= series.length - WINDOW_SIZE; start += 1) {
        let windowOccupancy = 0;
        let windowFlow = 0;
        for (let j = start; j < start + WINDOW_SIZE; j += 1) {
          windowOccupancy += series[j];
          windowFlow += stepFlow[j];
        }
        const windowFlowRate = windowFlow / WINDOW_SIZE;
        if (windowFlowRate > 0.5) {
          const windowDwell = Math.round((windowOccupancy / WINDOW_SIZE) / windowFlowRate);
          if (peakDwell === null || windowDwell > peakDwell) {
            peakDwell = windowDwell;
            peakMinuteIndex = start + Math.floor(WINDOW_SIZE / 2);
          }
        }
      }
    }

    estimates[zoneId] = {
      avg: avgDwell,
      peak: peakDwell,
      peakMinute: peakMinuteIndex !== null ? startMinute + peakMinuteIndex : null,
    };
  });

  return estimates;
};

const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

// Everything the Overview shows for one game
export const buildGameMetrics = ({ date, gameDate, occupancy, traffic, sales, queue, currentLocation, window }) => {
  const startMinute = toDayMinute(gameDate?.gameStartTime);
  const gatesMinute = toDayMinute(gameDate?.gatesOpen);
  // Time window, relative to game start (whole day when the start time is unknown)
  const windowStart = startMinute != null ? Math.max(0, startMinute + (window?.startOffset ?? -180)) : 0;
  const windowEnd = startMinute != null ? Math.min(1619, startMinute + (window?.endOffset ?? 240)) : 1619;
  const inWindow = (m) => m >= windowStart && m <= windowEnd;

  // Traffic
  let trafficMetrics = null;
  if (traffic && traffic.length) {
    const phases = gameDate?.gatesOpen && gameDate?.gameStartTime
      ? phaseCounts(traffic, date, gameDate.gatesOpen, gameDate.gameStartTime)
      : { pregame: 0, gates: 0, game: 0 };
    // New entries between readings, at each reading's minute
    const entries = [];
    for (let i = 1; i < traffic.length; i += 1) {
      const m = timestampMinute(traffic[i]?.timestamp, date);
      const diff = (traffic[i]?.count || 0) - (traffic[i - 1]?.count || 0);
      if (m != null && diff > 0) entries.push({ m, v: diff });
    }
    trafficMetrics = {
      inWindow: entries.filter((e) => inWindow(e.m)).reduce((s, e) => s + e.v, 0),
      phases,
      day: phases.pregame + phases.gates + phases.game,
      game: phases.gates + phases.game,
      entries,
    };
  }

  // Conversion: purchases (sales sensors' exits) over day traffic, as before
  let conversion = null;
  const salesIds = currentLocation.doorCounterSensorSalesIds || [];
  if (salesIds.length && sales && sales.length && trafficMetrics) {
    const last = sales[sales.length - 1];
    const purchases = salesIds.reduce((sum, id) => sum + (last[`${id}_out`] || 0), 0);
    const base = trafficMetrics.day;
    conversion = { purchases, base, rate: base > 0 ? Math.round((purchases / base) * 1000) / 10 : 0 };
  }

  // Occupancy
  let occupancyMetrics = null;
  if (occupancy && occupancy.length && currentLocation.maxOccupancy) {
    let peak = { total: 0, minute: null };
    occupancy.forEach((row, m) => {
      if (inWindow(m) && row.total > peak.total) peak = { total: row.total, minute: m };
    });
    const windowRows = occupancy.filter((_, m) => inWindow(m));
    const avg = mean(windowRows.map((r) => r.total));
    const zoneAvg = {};
    (currentLocation.zones || []).forEach((z) => {
      const vals = windowRows.map((r) => r.traffic.find((t) => t.id === z.id)?.traffic || 0);
      zoneAvg[z.id] = vals.length ? Math.round((mean(vals) || 0) * 10) / 10 : 0;
    });
    const busiest = Object.entries(zoneAvg).sort((a, b) => b[1] - a[1])[0];
    const zoneIds = (currentLocation.zones || []).map((z) => z.id);
    const dwell = estimateZoneDwell(occupancy, zoneIds, currentLocation.zoneAdjacency || null, windowStart, windowEnd);
    const longest = Object.entries(dwell)
      .filter(([, d]) => d?.avg != null)
      .sort((a, b) => b[1].avg - a[1].avg)[0];
    occupancyMetrics = {
      peak: { ...peak, perc: Math.round((peak.total / currentLocation.maxOccupancy) * 100) },
      avg: avg != null ? Math.round(avg) : 0,
      avgPerc: avg != null ? Math.round((avg / currentLocation.maxOccupancy) * 100) : 0,
      zoneAvg,
      busiestZone: busiest && busiest[1] > 0 ? { id: Number(busiest[0]), avg: busiest[1] } : null,
      dwell,
      longestDwell: longest ? { id: Number(longest[0]), ...longest[1] } : null,
      minutes: occupancy.map((r) => r.total),
    };
  }

  // Queue times: each reading averaged across queue locations
  let queueMetrics = null;
  if (queue && queue.length) {
    // One reading per minute: if a minute has two (e.g. every 30s), the later one is used
    const byMinute = new Map();
    [...queue]
      .filter((e) => e && e.timestamp)
      .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0))
      .forEach((e) => {
        const m = timestampMinute(e.timestamp, date);
        if (m != null && typeof e.queueTime === 'number') byMinute.set(m, e.queueTime);
      });
    const readings = [...byMinute.entries()].sort((a, b) => a[0] - b[0]).map(([m, v]) => ({ m, v }));
    const inWin = readings.filter((r) => inWindow(r.m));
    const peak = inWin.reduce((best, r) => (r.v > (best?.v ?? -1) ? r : best), null);
    queueMetrics = {
      avg: inWin.length ? Math.round((mean(inWin.map((r) => r.v)) || 0) * 10) / 10 : null,
      peak: peak ? { value: peak.v, minute: peak.m } : null,
      readings,
    };
  }

  return {
    date,
    startMinute,
    gatesMinute,
    windowStart,
    windowEnd,
    traffic: trafficMetrics,
    conversion,
    occupancy: occupancyMetrics,
    queue: queueMetrics,
  };
};

// Points for the comparison chart, bucketed so games line up.
// align 'start': x = minutes from game start; align 'clock': x = minute of the day.
export const bucketSeries = (points, { align, startMinute, size, agg = 'avg' }) => {
  const base = align === 'start' ? (startMinute ?? 0) : 0;
  const buckets = new Map();
  points.forEach(({ m, v }) => {
    const x = Math.floor((m - base) / size) * size;
    const b = buckets.get(x) || { sum: 0, n: 0 };
    b.sum += v;
    b.n += 1;
    buckets.set(x, b);
  });
  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([x, b]) => ({ x, y: agg === 'sum' ? b.sum : Math.round((b.sum / b.n) * 10) / 10 }));
};

// Occupancy minutes -> points, trimmed to when the store had people in it
export const occupancyPoints = (minutes) => {
  if (!minutes) return [];
  let first = minutes.findIndex((v) => v > 0);
  if (first < 0) return [];
  let last = minutes.length - 1;
  while (last > first && minutes[last] === 0) last -= 1;
  first = Math.max(0, first - 15);
  last = Math.min(minutes.length - 1, last + 15);
  const out = [];
  for (let m = first; m <= last; m += 1) out.push({ m, v: minutes[m] });
  return out;
};
